import React from 'react';
import { useTranslation } from 'react-i18next';
import useSeo, { ORGANIZATION_SCHEMA } from '../hooks/useSeo';
import { usePageContent } from './home/pageContent';
import { BoxGrid, BoxIcon, PageHero, PageShell, SectionIntro } from './home/blocks';
import { Highlight, Kicker, Paragraphs, assetUrl } from './home/ui';
import CtaBanner from './home/CtaBanner';

/* Built from the Figma "Functionaliteiten" blocks. Content: Admin → Landingspagina → Over ons. */
const OverOns = () => {
  const { t } = useTranslation();
  const { lang, cms, content } = usePageContent('about');

  useSeo({
    path: '/over-ons',
    lang,
    title: cms?.seo?.title || t('about.seo_title'),
    description: cms?.seo?.description || t('about.seo_description'),
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        ORGANIZATION_SCHEMA,
        { '@type': 'AboutPage', name: t('about.seo_title'), about: { '@type': 'Organization', name: 'WowProfit.io' } },
      ],
    },
  });

  return (
    <PageShell testId="over-ons-page">
      <PageHero hero={content.hero} />

      {/* Mission + why Belgium */}
      <SectionIntro />
      <section className="hp-container mt-[clamp(28px,4vw,72px)] grid grid-cols-1 gap-[clamp(24px,2.7778vw,50px)] lg:grid-cols-2" data-testid="about-story">
        {content.story.items.map((item, i) => (
          <div
            key={i}
            className={`hp-card hp-card-navy px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(40px,3.5556vw,64px)] lg:py-[clamp(48px,4.4444vw,80px)] ${i % 2 ? 'bg-brand-mint' : 'bg-white'}`}
          >
            <BoxIcon icon={item.icon} />
            <h2 className="hp-h2 mt-[clamp(20px,1.7778vw,32px)] text-[#273845]">
              <Highlight text={item.title} />
            </h2>
            <div className="hp-body mt-[clamp(16px,1.7778vw,32px)] space-y-4 text-[#273845]">
              <Paragraphs text={item.body} />
            </div>
          </div>
        ))}
      </section>

      {/* Team */}
      <SectionIntro title={content.team.title} />
      {content.team.subtitle && (
        <div className="hp-container">
          <p className="mx-auto mt-[clamp(14px,1.3333vw,24px)] max-w-[38.5em] text-center text-[clamp(16px,1.3333vw,24px)] font-medium leading-[1.5833] text-[#273845]">
            {content.team.subtitle}
          </p>
        </div>
      )}
      <section className="hp-container mt-[clamp(32px,4.0556vw,73px)]">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-[clamp(24px,2.7778vw,50px)] md:grid-cols-2">
          {content.team.members.map((m, i) => (
            <div
              key={i}
              data-testid={`founder-card-${(m.name || '').split(' ')[0].toLowerCase()}`}
              className="rounded-[clamp(20px,1.6667vw,30px)] border border-[#DBDBDB] bg-white px-[clamp(20px,2.2222vw,40px)] py-[clamp(28px,2.8889vw,52px)]"
            >
              {m.photo && (
                <img
                  src={assetUrl(m.photo)}
                  alt={m.name}
                  loading="lazy"
                  className="h-[clamp(96px,7.7778vw,140px)] w-[clamp(96px,7.7778vw,140px)] rounded-full object-cover ring-[6px] ring-brand-mint"
                />
              )}
              <h3 className="mt-[clamp(18px,1.6667vw,30px)] text-[clamp(24px,2.2222vw,40px)] font-bold leading-[1.2] text-[#273845]">{m.name}</h3>
              <Kicker className="mt-[clamp(12px,1.1111vw,20px)] text-brand-ink">{m.role}</Kicker>
              <p className="mt-[clamp(14px,1.3333vw,24px)] text-[clamp(16px,1.1111vw,20px)] font-medium leading-[1.7] text-[#273845]">{m.bio}</p>
            </div>
          ))}
        </div>
      </section>

      <BoxGrid id="waarden" title={content.values.title} items={content.values.items} />
      <div className="pt-[clamp(0px,5.3333vw,96px)]">
        <CtaBanner cta={content.cta} />
      </div>
    </PageShell>
  );
};

export default OverOns;
